import type { ButtonProps } from '@sravni/react-design-system/lib/Button';
import { useMemo } from 'react';
import { v7 as uuidv7 } from 'uuid';

import { PRODUCT_CATEGORIES_LABELS } from 'constants/labels';

import { enrichPixel, isPreApprovalOffer, isProduction, useAppSelector } from 'shared/lib';
import { sendEventPreApprovedOfferClick, sendEventPreApprovedOfferRedirect } from 'shared/lib/sendGAEvents';
import type { TOffer } from 'shared/types/TRankingService';

import { analyticsIdsSelector } from 'entities/analytics';
import { calcIdSelector } from 'entities/offers';
import { getStParentId } from 'entities/st-tracking';
import { userIdSelector } from 'entities/user';
import { selectTrafficSourceAsUtmURIString } from 'entities/utmMarks';

import { useSendOffer } from 'features/OfferControls';

import { OfferButton } from '../OfferButton/OfferButton';

import styles from './GoToRefOffer.module.scss';
import { GoToRefOfferTexts } from './GoToRefOffer.texts';

interface IGoToRefOfferProps {
  offer: TOffer;
  buttonProps?: ButtonProps;
}

export const GoToRefOffer: FC<IGoToRefOfferProps> = (props) => {
  const { offer, buttonProps } = props;
  const { onClick: onClickProp, ...otherButtonProps } = buttonProps || {};
  const isProductionValue = isProduction();
  const { sendReferralOffer, sendPreApprovalOffer } = useSendOffer('Ref');
  const { referralLink, displayPixelUrl } = offer.additionalInfo || {};
  const userId = useAppSelector(userIdSelector);
  const calcId = useAppSelector(calcIdSelector);
  const trafficSourceAsUtmURIString = useAppSelector(selectTrafficSourceAsUtmURIString);
  const { uaClientId, ymClientId } = useAppSelector(analyticsIdsSelector);

  const pixelData: Parameters<typeof enrichPixel>[1] = useMemo(
    () => ({
      userId: userId || '',
      bankName: offer.bankName,
      offerName: offer.productName,
      googleAnalyticId: uaClientId || '',
      yandexMetricsId: ymClientId || '',
      productName: PRODUCT_CATEGORIES_LABELS[offer.category!] || '',
      calcId,
      trafficSourceAsUtmURIString,
      source: `creditselection|position_${offer.order}`,
    }),
    [offer, userId, calcId, uaClientId, ymClientId, trafficSourceAsUtmURIString],
  );

  const pixelUrl = useMemo(() => enrichPixel(displayPixelUrl, pixelData), [displayPixelUrl, pixelData]);

  const onClick = () => {
    const uuid = uuidv7();
    if (isPreApprovalOffer(offer)) {
      if (!offer.loansStatus) {
        sendEventPreApprovedOfferClick({
          bankName: offer.bankName,
          offerName: offer.productName,
        });

        sendEventPreApprovedOfferRedirect({
          bankName: offer.bankName,
          offerName: offer.productName,
          redirectUrl: referralLink || '',
        });

        sendPreApprovalOffer(offer, { referralUrl: referralLink, uuid });
      }
    } else {
      sendReferralOffer(offer, { referralUrl: referralLink, uuid });
    }

    if (!referralLink) {
      return;
    }

    const stParentId = getStParentId();
    const url = enrichPixel(referralLink, {
      ...pixelData,
      uuid,
      extraQuery: {
        root_transaction_id: stParentId,
      },
    });

    window.open(url, '_blank');
  };

  return (
    <OfferButton
      {...otherButtonProps}
      variant="primary"
      color="dark"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
        onClickProp?.(event);
      }}
    >
      {pixelUrl &&
        (isProductionValue ? (
          <img
            alt=""
            loading="lazy"
            src={pixelUrl}
            className={styles.pixel}
          />
        ) : (
          <span
            className={styles.pixel}
            data-pixel={pixelUrl}
          />
        ))}
      {offer?.additionalInfo?.buttonText || GoToRefOfferTexts.submitButtonLabel}
    </OfferButton>
  );
};
